import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import SectionHeader from '@/components/ui/SectionHeader'
import ArchiveGrid from '@/components/ui/ArchiveGrid'
import ThemeRail from '@/components/ui/ThemeRail'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSEO } from '@/hooks/useSEO'
import type { Archive, Theme } from '@/types'
import * as api from '@/lib/api'

const discoverPaths = [
  {
    title: 'Text',
    description: 'Full chapters, biographies, and in-depth articles',
    path: '/archives',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    ),
  },
  {
    title: 'Podcasts',
    description: 'In-depth audio explorations of historical topics',
    path: '/podcasts',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
    ),
  },
  {
    title: 'YouTube',
    description: 'History explored as a conversation between two scholars',
    path: '/conversations',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    title: 'Artifacts',
    description: 'Collectible prints and medallions',
    path: '/artifacts',
    icon: (
      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    ),
  },
]

export default function ExplorePage() {
  // SEO
  useSEO({
    title: 'Explore',
    description: 'Explore The Archive Network — discover archives, cross-archive themes, podcasts, and documentaries connecting the stories that shaped human history.',
    url: 'https://thearchivenetwork.com/explore',
  })

  const [loading, setLoading] = useState(true)
  const [archives, setArchives] = useState<Archive[]>([])
  const [themes, setThemes] = useState<Theme[]>([])

  useEffect(() => {
    async function loadData() {
      try {
        const [archivesData, themesData] = await Promise.all([
          api.getArchives(),
          api.getThemes(),
        ])
        setArchives(archivesData)
        setThemes(themesData)
      } catch (error) {
        console.error('Failed to load explore data:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-archive-charcoal/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-4xl sm:text-5xl text-archive-cream">
            Explore The Archives
          </h1>
          <p className="mt-4 text-lg text-archive-muted max-w-2xl mx-auto">
            Discover interconnected histories across our growing network of specialized archives.
            Navigate by archive, theme, or content type.
          </p>
        </div>
      </section>

      {/* Discover Paths */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader title="Discover" centered />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {discoverPaths.map((path) => (
              <Link
                key={path.path}
                to={path.path}
                className="group artifact-frame p-6 text-center hover:border-archive-gold/40"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-archive-stone/50 flex items-center justify-center text-archive-gold group-hover:bg-archive-gold/10 transition-colors">
                  {path.icon}
                </div>
                <h3 className="font-display text-archive-cream text-xl group-hover:text-archive-gold transition-colors">
                  {path.title}
                </h3>
                <p className="mt-2 text-sm text-archive-muted">
                  {path.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Archives */}
      <section className="py-16 bg-archive-charcoal/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            title="The Archives"
            subtitle="Five interconnected collections spanning the breadth of human history."
            ctaText="View all archives"
            ctaLink="/archives"
          />
          <ArchiveGrid archives={archives} />
        </div>
      </section>

      {/* Themes */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            title="Themes"
            subtitle="Cross-cutting topics that span multiple archives."
            ctaText="View all themes"
            ctaLink="/themes"
          />
          <ThemeRail themes={themes.slice(0, 10)} />
        </div>
      </section>
    </div>
  )
}
